import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useReportWebVitals } from 'next/web-vitals';

import { logger } from '@zep/core-logger';

import { isProduction } from '@/consts/env';

import {
  buildWebVitalAttributes,
  isReportedMetric,
  isSampledSession,
  PRODUCTION_SAMPLE_RATE,
  WEB_VITAL_LOG_NAME,
  type WebVitalMetric,
} from '../utils';

type NavigatorWithConnection = Navigator & {
  connection?: { effectiveType?: string };
};

const readSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    // 사파리 사생활 보호 등으로 막히면 매번 새로 뽑는다.
    return undefined;
  }
};

/**
 * 실사용자 속도 지표(TTFB·FCP·LCP·INP·CLS)를 ClickHouse 로만 보낸다.
 * `logger.debug` 는 HyperDX 로거에만 간다 — `trackEvent` 는 PostHog·GA4·GTM 까지 퍼진다.
 */
export const useQuizWebVitals = () => {
  const { pathname } = useRouter();
  // 갱신하지 않는다: 지표는 문서 단위라, 클라이언트 이동 뒤 확정돼도 진입 페이지 몫이다.
  const entryRoute = useRef(pathname).current;

  // useReportWebVitals 는 콜백이 바뀌면 관찰을 다시 등록해 중복 보고가 난다 → 한 번만 만든다.
  const reportRef = useRef((metric: WebVitalMetric) => {
    if (!isReportedMetric(metric.name)) return;
    const sampleRate = isProduction ? PRODUCTION_SAMPLE_RATE : 1;
    if (!isSampledSession(readSessionStorage(), sampleRate)) return;

    logger.debug(
      WEB_VITAL_LOG_NAME,
      buildWebVitalAttributes(metric, {
        route: entryRoute,
        effectiveType: (navigator as NavigatorWithConnection).connection
          ?.effectiveType,
      }),
    );
  });

  useReportWebVitals(reportRef.current);
};
